// ============================================================================
// src/lib/sheepAnimalOperationApi.ts — the one client of public.sheep_animal_operation
// ----------------------------------------------------------------------------
// Migration 244 (R-H sheep) replaces direct PostgREST writes to public.sheep and
// public.sheep_lambing_records across SheepFlocksView ('Add sheep'),
// SheepAnimalPage (inline field edits, prior-tags edits, tag retags, lambing add),
// WeighInSessionPage (sheep tag swap and new-tag reconcile), and SheepBulkImport
// with ONE audited server entrypoint.
//
// WHAT THIS MODULE OWNS: the transport and the meaning of every reply.
//
//   * REQUEST IDS. One per user action (`newSheepAnimalRequestId`). A retry of
//     the same action reuses it: the server replays the stored result or applies
//     the operation once.
//   * RETRIES. A transport failure (no reply, timeout) is retried here with the
//     SAME id, which the receipt makes safe. A database answer (any SQLSTATE)
//     is never retried.
//   * EXPECTED VALUES. The caller sends the value it READ from the loaded row,
//     never a form value; the server compares JSON under a row lock.
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';
import {newClientSubmissionId} from './clientSubmissionId.js';

export type SheepAnimalOperation =
  | 'animal.create'
  | 'animal.patch'
  | 'animal.retag'
  | 'lambing.create'
  | 'import.sheep'
  | 'import.lambing';

type Json = unknown;

export type SheepAnimalOpTarget = Record<string, never> | {sheep_id: string} | {dam_sheep_id: string};

export type SheepAnimalOpRequest = {
  requestId: string;
  operation: SheepAnimalOperation;
  target: SheepAnimalOpTarget;
  expected?: Record<string, Json>;
  change: Record<string, Json>;
};

export type SheepAnimalConflict = {field: string; expected: Json; current: Json};

export type SheepAnimalOpOutcome =
  | {
      status: 'saved';
      kind: 'created' | 'updated';
      written: Record<string, Json>;
      eventIds: string[];
      replayed: boolean;
    }
  | {status: 'no_change'}
  | {status: 'conflict'; conflicts: SheepAnimalConflict[]}
  | {status: 'not_found'}
  | {status: 'refused'; reason: string}
  | {status: 'rejected'; code: string; message: string}
  | {status: 'unknown'; message: string};

/** A fresh id for ONE user action. Keep it with the action and reuse it on retry. */
export function newSheepAnimalRequestId(prefix = 'sheep-animal-'): string {
  return prefix + newClientSubmissionId();
}

const ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 8000;
// A five-character SQLSTATE (22023, 23505, 42501, WCF01, ...) is the database's answer.
const SQLSTATE = /^[0-9A-Z]{5}$/;
const RETRYABLE_GATEWAY_STATUS = new Set([0, 502, 503, 504]);

type RpcError = {code?: string; message?: string; status?: number} | null;

// L1 (REVIEW-244-v1): retry only on a code-less transport/gateway failure
// (status 0, 502, 503, 504, or a thrown fetch without status/code). Never
// retry a SQLSTATE or a PostgREST PGRST... error.
function isRetryableTransportError(error: NonNullable<RpcError>): boolean {
  if (typeof error.code === 'string' && error.code.trim() !== '') {
    return false;
  }
  if (typeof error.status === 'number') {
    return RETRYABLE_GATEWAY_STATUS.has(error.status);
  }
  return true;
}

/** Read one reply. Anything that does not answer THIS request is not a receipt. */
export function interpretSheepAnimalReply(request: SheepAnimalOpRequest, data: unknown): SheepAnimalOpOutcome {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, Json>;
  if (d.request_id !== request.requestId || d.operation !== request.operation) {
    return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.'};
  }
  switch (d.kind) {
    case 'created':
    case 'updated':
      if (d.ok !== true) break;
      return {
        status: 'saved',
        kind: d.kind,
        written: (d.written && typeof d.written === 'object' ? d.written : {}) as Record<string, Json>,
        eventIds: Array.isArray(d.event_ids) ? (d.event_ids as string[]) : [],
        replayed: d.replayed === true,
      };
    case 'no_change':
      return {status: 'no_change'};
    case 'conflict':
      return {
        status: 'conflict',
        conflicts: Array.isArray(d.conflicts) ? (d.conflicts as SheepAnimalConflict[]) : [],
      };
    case 'not_found':
      return {status: 'not_found'};
    case 'refused':
      return {status: 'refused', reason: typeof d.reason === 'string' ? d.reason : 'refused'};
  }
  return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.'};
}

/**
 * Apply one sheep animal operation. Never throws. Transport failures are retried
 * with the same request id; a database or PostgREST answer comes back at once.
 */
export async function applySheepAnimalOperation(
  sb: SupabaseClient,
  request: SheepAnimalOpRequest,
): Promise<SheepAnimalOpOutcome> {
  let lastMessage = 'No reply from the server.';
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const call = sb.rpc('sheep_animal_operation', {
        p_request_id: request.requestId,
        p_operation: request.operation,
        p_target: request.target,
        p_expected: request.expected ?? {},
        p_change: request.change,
      });
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
      });
      const {data, error} = (await Promise.race([call, timeout])) as {data: unknown; error: RpcError};
      if (error) {
        if (error.code && SQLSTATE.test(error.code)) {
          return {status: 'rejected', code: error.code, message: error.message || 'The change was refused.'};
        }
        if (!isRetryableTransportError(error)) {
          return {
            status: 'rejected',
            code: (typeof error.code === 'string' && error.code.trim()) || String(error.status ?? 'ERROR'),
            message: error.message || 'The change was refused.',
          };
        }
        throw new Error(error.message || 'transport');
      }
      return interpretSheepAnimalReply(request, data);
    } catch (e) {
      lastMessage = (e as {message?: string}).message || lastMessage;
      if (attempt < ATTEMPTS) await new Promise((r) => setTimeout(r, 1000 * attempt));
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }
  console.warn('sheep_animal_operation: no confirmed reply after retries:', lastMessage);
  return {status: 'unknown', message: 'Could not reach the server. Your change is kept; retry.'};
}

/** The sentence for an outcome that did not save, in the page's own terms. */
export function sheepAnimalOutcomeMessage(
  outcome: Exclude<SheepAnimalOpOutcome, {status: 'saved'} | {status: 'no_change'}>,
): string {
  switch (outcome.status) {
    case 'conflict':
      return 'Changed by someone else since this page loaded. Reload to see the current values.';
    case 'not_found':
      return 'This sheep was removed elsewhere. Reload to see the current list.';
    case 'refused':
      if (outcome.reason === 'not_found') return 'This sheep was removed elsewhere. Reload to see the current list.';
      return 'The change was refused (' + outcome.reason + ').';
    case 'rejected':
      switch (outcome.code) {
        case '22023':
          return outcome.message.replace(/^sheep_animal_operation: /, '');
        case '23505':
          return outcome.message;
        case '42501':
          return 'Your session or role changed. Sign in again; your change is kept.';
        case 'WCF01':
          return 'This change no longer matches what was saved. Reload and try again.';
        default:
          return outcome.message || 'The change was refused.';
      }
    case 'unknown':
      return outcome.message;
  }
}

/** An operation that did not save, as the error the catalog's callers catch. */
export class SheepAnimalOperationError extends Error {
  readonly outcome: Exclude<SheepAnimalOpOutcome, {status: 'saved'} | {status: 'no_change'}>;
  readonly code?: string;
  constructor(outcome: Exclude<SheepAnimalOpOutcome, {status: 'saved'} | {status: 'no_change'}>) {
    super(sheepAnimalOutcomeMessage(outcome));
    this.name = 'SheepAnimalOperationError';
    this.outcome = outcome;
    if (outcome.status === 'rejected') this.code = outcome.code;
  }
}

/** Run one operation; resolve on a saved or unchanged outcome, throw otherwise. */
export async function runSheepAnimalOperation(
  sb: SupabaseClient,
  request: SheepAnimalOpRequest,
): Promise<Extract<SheepAnimalOpOutcome, {status: 'saved'} | {status: 'no_change'}>> {
  const outcome = await applySheepAnimalOperation(sb, request);
  if (outcome.status === 'saved' || outcome.status === 'no_change') return outcome;
  throw new SheepAnimalOperationError(outcome);
}

/** Create one sheep animal from SheepFlocksView ('Add sheep'). */
export async function createSheepAnimalOp(
  sb: SupabaseClient,
  change: Record<string, Json>,
  requestId = newSheepAnimalRequestId('sheep-create-'),
) {
  return runSheepAnimalOperation(sb, {
    requestId,
    operation: 'animal.create',
    target: {},
    expected: {},
    change,
  });
}

/** Patch inline fields (except tag and flock) on a live sheep row under field-level CAS. */
export async function patchSheepAnimalOp(
  sb: SupabaseClient,
  sheepId: string,
  expected: Record<string, Json>,
  change: Record<string, Json>,
  requestId = newSheepAnimalRequestId('sheep-patch-'),
) {
  return runSheepAnimalOperation(sb, {
    requestId,
    operation: 'animal.patch',
    target: {sheep_id: sheepId},
    expected,
    change,
  });
}

export type RetagSheepArgs = {
  sheepId: string;
  expectedTag: string | null | undefined;
  newTag: string | null;
  source: 'manual' | 'weigh_in';
  /** H1: the tag the operator typed — required for the weigh_in source. */
  priorTag?: string;
  requestId?: string;
};

export type SheepOpOutcome =
  | {
      status: 'saved';
      kind: string;
      replayed: boolean;
      mustReread: boolean;
      eventIds: string[];
      written?: Record<string, Json>;
    }
  | {status: 'no_change'}
  | {status: 'conflict'; conflicts: unknown[]}
  | {status: 'rejected'; code: string; message: string}
  | {status: 'unknown'; message: string; mustReread: true};

/**
 * Retag a live sheep row under row lock, composing old_tags server-side for
 * either 'manual' (SheepDetail inline tag edit) or 'weigh_in' (WeighInSessionPage).
 */
export async function retagSheepAnimalOp(sb: SupabaseClient, args: RetagSheepArgs): Promise<SheepOpOutcome> {
  const reqId = args.requestId || newSheepAnimalRequestId('sheep-retag-');
  const change: Record<string, Json> =
    args.source === 'weigh_in'
      ? {tag: args.newTag, source: args.source, prior_tag: args.priorTag ?? null}
      : args.priorTag !== undefined
        ? {tag: args.newTag, source: args.source, prior_tag: args.priorTag}
        : {tag: args.newTag, source: args.source};
  const outcome = await applySheepAnimalOperation(sb, {
    requestId: reqId,
    operation: 'animal.retag',
    target: {sheep_id: args.sheepId},
    expected: args.expectedTag === undefined ? {} : {tag: args.expectedTag},
    change,
  });
  switch (outcome.status) {
    case 'saved':
      return {
        status: 'saved',
        kind: outcome.kind,
        replayed: outcome.replayed,
        mustReread: outcome.replayed,
        eventIds: outcome.eventIds,
        written: outcome.written,
      };
    case 'no_change':
      return {status: 'no_change'};
    case 'conflict':
      return {status: 'conflict', conflicts: outcome.conflicts};
    case 'not_found':
      return {
        status: 'rejected',
        code: 'not_found',
        message: 'This sheep was removed elsewhere. Reload to see the current list.',
      };
    case 'refused':
      return {
        status: 'rejected',
        code: outcome.reason,
        message: sheepAnimalOutcomeMessage(outcome),
      };
    case 'rejected':
      return outcome;
    case 'unknown':
      return {status: 'unknown', message: outcome.message, mustReread: true};
  }
}

/** Record a lambing row on a live dam sheep and emit record.created on sheep.animal. */
export async function createSheepLambingOp(
  sb: SupabaseClient,
  damSheepId: string,
  change: Record<string, Json>,
  requestId = newSheepAnimalRequestId('sheep-lambing-'),
) {
  return runSheepAnimalOperation(sb, {
    requestId,
    operation: 'lambing.create',
    target: {dam_sheep_id: damSheepId},
    expected: {},
    change,
  });
}

/** Import one sheep row from SheepBulkImport (no Activity emitted per S1). */
export async function importSheepAnimalOp(
  sb: SupabaseClient,
  change: Record<string, Json>,
  requestId = newSheepAnimalRequestId('sheep-imp-s-'),
): Promise<SheepAnimalOpOutcome> {
  return applySheepAnimalOperation(sb, {
    requestId,
    operation: 'import.sheep',
    target: {},
    expected: {},
    change,
  });
}

/** Import one lambing row from SheepBulkImport (no Activity emitted per S1). */
export async function importSheepLambingOp(
  sb: SupabaseClient,
  change: Record<string, Json>,
  requestId = newSheepAnimalRequestId('sheep-imp-l-'),
): Promise<SheepAnimalOpOutcome> {
  return applySheepAnimalOperation(sb, {
    requestId,
    operation: 'import.lambing',
    target: {},
    expected: {},
    change,
  });
}
